import { ReactComponent as CloseIconSvg } from "@/src/assets/icons/icon-cancle-black.svg";
import ChevronLeft from "@/src/assets/icons/icon-chevron-left-black.svg";
import DotGray4Svg from "@/src/assets/icons/icon-dot-gray4.svg";
import DotGray7Svg from "@/src/assets/icons/icon-dot-gray7.svg";
import { ReactComponent as RedoSvg } from "@/src/assets/icons/icon-redo-blue.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import BookMarkStatus from "@/src/components/molecule/BookMarkStatus";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import Flex from "@/src/components/molecule/Flex";
import ImporterMainLayout from "@/src/components/template/Layout/importer/ImporterMainLayout";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { PartialCommonCodeItemDto } from "@/src/store/apis/common/interface";
import {
  useGetSaleQuery,
  useGetSalesDetailBookmarkQuery,
  useSalesCloseMutation,
  useSalesRedoMutation,
  useUpdateSalesDetailBookmarkMutation,
} from "@/src/store/apis/sales";
import colorSet, { ColorType } from "@/src/styles/color";
import { isUndefined } from "@/src/utils/is";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import SalesDetailContainerList from "./components/detail/SalesDetailContainerList";
import SalesDetailInformation from "./components/detail/SalesDetailInformation";
import SalesDetailMemo from "./components/detail/SalesDetailMemo";
import SalesDetailStatistics from "./components/detail/SalesDetailStatistics";
import renderSalesStatusBadge from "./utils/renderSalesStatusBadge";
import { useCompanyLocalDate } from "@/src/hooks/useCompanyLocalDate";

enum DialogState {
  NULL,
}

const mainCategoryEmptyArray: PartialCommonCodeItemDto[] = [];

const ImporterSalesDetailPage = () => {
  const { t } = useTranslation();
  const alert = useAlert();
  const params = useParams();
  const navigate = useNavigate();
  const loadingTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();

  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  // API
  const { mainCategoryList } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "MAIN_CATEGORY",
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          mainCategoryList: isStable
            ? (currentData ?? mainCategoryEmptyArray)
            : mainCategoryEmptyArray,
        };
      },
    }
  );
  const { exporterUserType, userMainCategory } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        userMainCategory: isStable
          ? currentData.row.mainCategoryCodeItemNames
          : [],
        exporterUserType: currentData?.row.exporterUserType,
      };
    },
  });
  const [salesRedo, { isLoading: isSalesRedoLoading }] = useSalesRedoMutation();
  const [salesClose, { isLoading: isSalesCloseLoading }] =
    useSalesCloseMutation();
  const {
    isSaleDetailFetching,
    salesWriterName,
    salesCreatedAt,
    lastEditorName,
    salesUpdatedAt,
    salesStatus,
    salesNumber,
    salesMainCategory,
    isSalesError,
  } = useGetSaleQuery(
    { id: Number(params.id) },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isUndefined(currentData) || isError || isFetching;
        const isStable = !isUnstable;

        return {
          isSalesError: isError,
          isSaleDetailFetching: isFetching,
          salesStatus: currentData?.salesStatus,
          salesWriterName: isStable
            ? currentData.exporterUser?.name || "-"
            : "-",
          salesNumber: isStable ? currentData.salesNumber || "-" : "-",
          salesCreatedAt: isStable ? currentData.createdAt : "",
          salesUpdatedAt: isStable ? currentData.updatedAt : "",
          lastEditorName: isStable ? currentData.lastEditor?.name || "-" : "-",
          salesMainCategory: isStable
            ? currentData.exporterItem.mainCategoryCodeItemName
            : "-",
        };
      },
    }
  );
  const [editContractBookmark] = useUpdateSalesDetailBookmarkMutation();

  const { isBookmarked } = useGetSalesDetailBookmarkQuery(
    { id: Number(params.id) },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData }) => {
        return { isBookmarked: currentData?.isBookmarked };
      },
    }
  );

  const salesCreatedDate = useCompanyLocalDate({
    date: salesCreatedAt,
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const salesUpdatedDate = useCompanyLocalDate({
    date: salesUpdatedAt,
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const mainCategoryItemName = mainCategoryList.find(
    (item) => item.codeItemName === salesMainCategory
  )?.codeItemName;

  // 매니저,중간관리자 카테고리 비교
  const isMainCategoryAuthorized =
    exporterUserType === "MANAGER" || exporterUserType === "MIDDLE_MANAGER"
      ? userMainCategory.includes(mainCategoryItemName as string)
      : true;

  /**
   * 수정 권한
   * - 기업관리자, 중간관리자, 작성자
   * - 일반 매내저는 메인카테고리가 포함된 사람
   */
  const isEditAuth = !isSalesError && isMainCategoryAuthorized;

  const handleBookmarkClick = async () => {
    const bookmark = !isBookmarked;

    try {
      const bookmarkParams: { id: number; isBookmarked: boolean } = {
        id: Number(params.id),
        isBookmarked: bookmark,
      };

      await editContractBookmark(bookmarkParams).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleOrderClosingClick = async () => {
    try {
      await salesClose({ id: Number(params.id) }).unwrap();
      alert.showAlert({ type: "success", message: t("alert:orderClosing") });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleSalesRedoClick = async () => {
    try {
      await salesRedo({ id: Number(params.id) }).unwrap();
      alert.showAlert({ type: "success", message: t("alert:orderRedo") });
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const renderDialogs = () => {
  };

  useEffect(() => {
    if (isSaleDetailFetching) {
      const setTimeoutId = setTimeout(() => {
        handleContentLoadingOn();
      }, 300);

      loadingTimeoutId.current = setTimeoutId;
    } else {
      if (loadingTimeoutId.current) {
        clearTimeout(loadingTimeoutId.current);
      }
      handleContentLoadingOff();
    }
  }, [isSaleDetailFetching, handleContentLoadingOff, handleContentLoadingOn]);

  return (
    <ImporterMainLayout
      breadcrumb={[t("sideNav:sales"), t("sideNav:detailSales")]}
      pageTitle={`${t("common:contractNo")} ${salesNumber}`}
    >
      <Flex flexDirection="column" isFullWidth gap={16}>
        <StyledFlex justifyContent="space-between">
          {salesStatus &&
            renderSalesStatusBadge({
              type: salesStatus,
              size: "L",
            })}

          {/* BookMark */}
          <BookMarkStatus
            bookMarkStatus={isBookmarked}
            bookMarkCount={0}
            onClickBookMark={handleBookmarkClick}
            onClickCount={() => {}}
            isEditAuth={isEditAuth}
          />
        </StyledFlex>

        {/* Content */}
        <SalesDetailStatistics />
        <Flex gap={16} isFullWidth>
          <SalesDetailInformation isEditAuth={isEditAuth} />
          <SalesDetailMemo isEditAuth={isEditAuth} />
        </Flex>
        <SalesDetailContainerList isEditAuth={isEditAuth} />

        {/* Last Editor */}
        <EditorContainer>
          <figure>
            <Icon iconSrc={DotGray7Svg} iconSize={4} />
            <Typo color="gray6" typoType="b9r">
              {`${t("sales:detail.editor.registrant")} (${t(
                "sales:detail.editor.registrantAt"
              )}) ${salesWriterName}`}
            </Typo>
            <Typo color="gray7" typoType="b9r">
              {salesCreatedDate}
            </Typo>
          </figure>
          <figure>
            <Icon iconSrc={DotGray4Svg} iconSize={4} />
            <Typo color="gray4" typoType="b9r">
              {`${t("sales:detail.editor.lastEditor")} (${t(
                "sales:detail.editor.lastEditorAt"
              )}) ${lastEditorName}`}
            </Typo>
            <Typo color="gray7" typoType="b9r">
              {salesUpdatedDate}
            </Typo>
          </figure>
        </EditorContainer>
      </Flex>

      {/* Bottom Fixed */}
      <BottomFixedContainer>
        <BottomButtonContainer>
          <StyledButton
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={() => navigate(-1)}
          >
            <Icon iconSrc={ChevronLeft} iconSize={16} />
            {t("sales:detail.button.backToPreviousButton")}
          </StyledButton>

          <figure>
            {salesStatus === "PROCESSING" && (
              <StyledButton
                buttonColor="red"
                buttonGrade="secondary"
                onClick={handleOrderClosingClick}
                isLoading={isSalesCloseLoading}
                disabled={isSalesCloseLoading || !isEditAuth}
              >
                <CloseIcon data-disabled={!isEditAuth} />
                {t("sales:detail.button.orderClosing")}
              </StyledButton>
            )}
            {salesStatus === "COMPLETE" && (
              <StyledButton
                buttonColor="blue"
                buttonGrade="secondary"
                onClick={handleSalesRedoClick}
                isLoading={isSalesRedoLoading}
                disabled={isSalesRedoLoading || !isEditAuth}
              >
                <RedoIcon data-disabled={!isEditAuth} />
                {t("sales:detail.button.RedoSales")}
              </StyledButton>
            )}
          </figure>
        </BottomButtonContainer>
      </BottomFixedContainer>

      {renderDialogs()}
    </ImporterMainLayout>
  );
};

export default ImporterSalesDetailPage;

const StyledFlex = styled(Flex)`
  padding-bottom: 18px;
`;

const BottomButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  figure {
    display: flex;
    gap: 8px;

    .temporary-save {
      width: 136px;
      justify-content: center;
    }
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CloseIcon = styled(CloseIconSvg)`
  width: 16px;
  height: 16px;

  path {
    fill: ${colorSet.red2};
  }

  &[data-disabled="true"] {
    path {
      fill: ${colorSet.red6};
    }
  }
`;

const RedoIcon = styled(RedoSvg)<{
  color?: ColorType;
  disabled?: boolean;
}>`
  width: 16px;
  height: 16px;

  path {
    ${({ color }) =>
      color &&
      css`
        fill: ${colorSet[color]};
      `}

    ${({ disabled }) =>
      disabled &&
      css`
        fill: ${colorSet.gray8};
      `}
  }
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 72px;

  figure {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;
